<template>
<div :id="componentId">
  <div class="toolbar mb8">
    <select class="custom-select ib" v-model="selectedUsername"
        @change="onChangeUsername">
      <option value="">user: 全て</option>
      <option v-for="e in usernames" :value="e.key" :key="e.key">
        {{e.val}}
      </option>
    </select>
    <sort-button class="ib sort-bt1 ml4" @sort="onDtSort"
        :toggle-type="sort.dt.toggleType">
      登録日
    </sort-button>
  </div>
  <div :id="listWrapId" class="list-wrap">
    <b-list-group class="selectable">
      <b-list-group-item v-for="e in displayedJoints" :key="e.id"
        :id="listItemId(e)"
        :class="getListItemClz(e)"
        @click="onListItemClick(e)"
        v-html="itemDisp(e)">
      </b-list-group-item>
    </b-list-group>
  </div>
  <pager class="mt8"
    :item-count="itemCount"
    :items-per-page="itemsPerPage"
    :page-range="1"
    :margin-pages="0"
    :force-page="currentPage - 1"
    @click="onChangePage"
  ></pager>
</div>
</template>

<script>
import Vue from 'vue'
import sortMixin from '@/mixin/sortMixin'
import * as eventNames from '@/consts/eventNames'
import * as sortStates from '@/consts/sortStates'

const dtFormatUTC = Vue.filter('dtFormatUTC')

export default {
  name: 'joint-list',
  props: {
    src: {
      type: Array,
      default() {
        return []
      }
    },
    dispType: {
      type: String,
      default: 'full',
    },
    stream: {
      type: Object,
    }
  },
  data() {
    return {
      isReady: false,

      filteredJoints: [],
      displayedJoints: [],
      allJoints: [],
      jointsByUsername: {},
      selectedJointsMap: {},

      usernames: [],
      selectedUsername: '',

      sort: {
        dt: {
          toggleType: sortStates.TOGGLE_TYPE_AD,
          state: sortStates.SORT_STATE_ASC,
        }
      },

      onResizeFunc: null,

      itemsPerPage: 2000,
      currentPage: 1,
    }
  },
  computed: {
    componentId() {
      return `joint-list-${this._uid}`
    },
    listWrapId() {
      return `list-wrap-${this._uid}`
    },
    itemCount() {
      return this.filteredJoints.length
    },
  },
  watch: {
    src() {
      const { joints, jointsByUsername, usernames } =
        this.convJoints(this.src.slice())
      this.allJoints = joints
      this.jointsByUsername = jointsByUsername
      this.usernames = usernames
      this.selectedUsername = ''
      this.isReady = true
      this.onChangeUsername()
    }
  },
  mounted() {
    this.onResizeFunc = () => {
      const h = window.innerHeight - 170
      const elem = document.getElementById(this.listWrapId)
      elem.style.height = `${h}px`
    }
    this.onResizeFunc()
    window.addEventListener('resize', this.onResizeFunc)
    this.setListeners()
  },
  destroyed() {
    window.removeEventListener('resize', this.onResizeFunc)
  },
  mixins: [sortMixin],
  methods: {
    listItemId(e) {
      return `list-item-${e.id}-${this._uid}`
    },
    getListItemClz(e) {
      // order matters
      const arr = []
      if (e.joint_number) {
        arr.push('attached')
      }
      if (this.selectedJointsMap[e.id]) {
        arr.push('selected')
      }
      return arr.join(' ')
    },
    setListeners() {
      this.stream.recv(eventNames.SELECT_JOINTS,
        ({ ids }) => {
          if (!this.isReady) { return }
          if (!ids || ids.length === 0) { return }
          const obj = ids.reduce((acc, id) => {
            acc[id] = true; return acc
          }, {})
          this.selectedJointsMap = obj
          this.scrollToItemById(ids[0])
        }
      )
      this.stream.recv(eventNames.UNSELECT_JOINTS,
        ({ ids }) => {
          if (!this.isReady) { return }
          if (!ids || ids.length === 0) { return }
          ids.forEach(id => {
            Vue.delete(this.selectedJointsMap, id)
          })
        }
      )
      this.stream.recv(eventNames.UPDATED_JOINTS,
        ({ joints }) => {
          if (!joints || joints.length === 0) { return }
          // update完了後は選択を解除したい
          joints.forEach(joint => {
            Vue.delete(this.selectedJointsMap, joint.id)
            this.stream.send(eventNames.UNSELECT_JOINTS,
              { ids: [joint.id] })
          })
        }
      )
    },
    scrollToItemById(id) {
      window.scrollManager.scrollTo(
        '#' + this.listItemId({ id }),
        200,
        {
          offset: -10,
          container: '#' + this.listWrapId,
        }
      )
    },
    itemDisp(e) {
      if (this.dispType === 'full') {
        return this.fullItemDisp(e)
      } else {
        return 'Wrong disp type'
      }
    },
    fullItemDisp(e) {
      return `
        <div class="item-disp">
          <p class="item-disp-row row1">
            【${e.joint_number ? '登録済' : '未登録'}】
            user: ${e.username}
          </p>
          <p class="item-disp-row row2">
            登録日: ${dtFormatUTC(e.created_at, 'yyyy-mm-dd HH:MM:SS')}
            <br>
            更新日: ${dtFormatUTC(e.updated_at, 'yyyy-mm-dd HH:MM:SS')}
          </p>
          <p class="item-disp-row row3">
            備考: ${e.bikou1 || 'なし'}
          </p>
        </div>
      `
    },
    convJoints(arr) {
      arr.sort((a, b) => {
        let v1 = new Date(a.created_at)
        let v2 = new Date(b.created_at)
        if (v1 < v2) {
          return -1
        } else if (v1 > v2) {
          return 1
        } else {
          v1 = new Date(a.updated_at)
          v2 = new Date(b.updated_at)
          return v1 < v2 ? -1 : (v1 > v2 ? 1 : 0)
        }
      })
      const joints = arr
      const jointsByUsername = {}
      const usernameObj = {}
      for (const e of arr) {
        usernameObj[e.username] = 1

        if (!jointsByUsername[e.username]) {
          jointsByUsername[e.username] = []
        }
        jointsByUsername[e.username].push(e)
      }

      const usernames = Object.keys(usernameObj).sort().map(e => {
        return {
          key: e,
          val: 'user: ' + e,
        }
      })

      return { joints, jointsByUsername, usernames }
    },
    onChangeUsername() {
      this.currentPage = 1
      this.updateFilteredJoints()
      const elem = document.getElementById(this.listWrapId)
      elem.scroll(0, 0)
    },
    onChangePage(page) {
      this.currentPage = page
      this.setDisplayedJoints()
      const elem = document.getElementById(this.listWrapId)
      elem.scroll(0, 0)
    },
    updateFilteredJoints() {
      let joints = []
      if (!this.selectedUsername) {
        joints = this.allJoints
      } else {
        joints = this.jointsByUsername[this.selectedUsername]
      }
      this.filteredJoints = this.sortedArrayBy(joints,
        { prop: 'created_at', sortState: this.sort.dt.state })
      this.setDisplayedJoints()
    },
    setDisplayedJoints() {
      const startIdx = this.itemsPerPage * Math.max(this.currentPage - 1, 0)
      this.displayedJoints = this.filteredJoints.slice(
        startIdx, startIdx + this.itemsPerPage)
      this.stream.send(eventNames.CHANGE_JOINTS,
        { joints: this.displayedJoints })
    },
    onListItemClick(e) {
      if (this.selectedJointsMap[e.id]) {
        // deselect
        Vue.delete(this.selectedJointsMap, e.id)
        this.stream.send(eventNames.UNSELECT_JOINTS, { ids: [e.id] })
        return
      }

      this.selectedJointsMap = { [e.id]: e }
      this.stream.send(eventNames.SELECT_JOINTS, { ids: [e.id] })
    },
    onDtSort({ sortState }) {
      this.sort.dt.state = sortState
      this.updateFilteredJoints()
    },
  }
}
</script>

<style lang="scss" scoped>
.custom-select {
  width: 70%;
}
.sort-bt1 {
  width: 27%;
}
</style>
